import { useEffect, useMemo, useRef, useState } from 'react';

import { BRAZIER_LIGHTING_ENABLED, BRAZIER_LIGHT_RADIUS } from '../lighting/brazierLightingConfig';
import { isWalkable } from '../map/isWalkable';
import { isTileCorrupted } from '../map/corruptionSystem';
import { pickBarredDoorPopupText } from '../map/barredDoors';
import {
  RANSACKED_CHEST_TILE,
  RANSACKED_CHEST_RELIC_ID,
  isRansackedChestExclusiveRelic,
} from '../map/ransackedChest';
import {
  getSpecialBossFloorEnemyIdAtTile,
  getMonsignorBossHpReduction,
  getPrimeVesselBossHpReduction,
  getPrimeVesselCombatStatusMods,
  MONSIGNOR_BOSS_ENEMY_ID,
  PRIME_VESSEL_BOSS_ENEMY_ID,
  MONSIGNOR_LIBERATED_GUARD_TILE,
  MONSIGNOR_LIBERATED_PENITENT_LORE,
  BLACKHAND_CONTRACT_LORE,
  BLACKHAND_CHEST_POS,
  BLACKHAND_BOSS_AMBUSH_POS,
  isBlackhandBossEncounterSelection,
} from '../boss/specialBossFloorConfig';
import {
  getEnemyIdForTile,
  CORRUPTED_SPECIAL_ENEMY_ID,
} from '../enemies/dungeonEnemySelection';
import { relicsPool } from '../data/relics';
import { getRelicRarity, pickRelicsByRarity } from '../relics/relicRarity';
import {
  initShrinesForMap,
  formatShrineCost,
  canAffordShrineCost,
} from '../shrine/shrineSystem';
import { getPlaceholderShrineReward } from '../shrine/shrineRewards';
import { computeCombatMods, applyChestHooks } from '../../relicEffects';

import { makeRng } from '../../utils/makeRng';
import { getLoreEntryForTile } from '../lore/loreSystem';
import {
  getActiveSanctifiedStatueSecret,
  getSanctifiedSecretLoreText,
  isCurrentTileSanctifiedSecretTargetStatue,
  isSanctifiedSecretLoreTile,
  markSanctifiedSecretClaimed,
  markSanctifiedSecretLoreRead,
  setSanctifiedSecretOfferPending,
} from '../map/sanctifiedStatueSecret';

const SIMPLE_CHEST_RESOURCE_KINDS = ['keys', 'potions', 'teeth', 'torches'];

export function useDungeonActions({

  initialPendingRansackedChestPos = null,
initialPendingWellMythicPos = null,
initialIsWellEchoOpen = false,

  map,
  setMap,
  mapWidth,
  mapHeight,
  playerPos,
  setPlayerPos,
  floorLevel,
  runSeedRef,
  barredDoorKeys,
  floorCorruption,

  isStatusOpen,
  isMerchantOpen,
  isMiniMapOpen,
  isStorylineActive,
  isCombatActive,
  isPreCombatTransitionActive,
  pendingDeckCullReward,

  keys,
  setKeys,
  maxKeys,
  teeth,
  setTeeth,
  maxTeeth,
  potions,
  setPotions,
  maxPotions,
  torches,
  setTorches,
  maxTorches,
  setHp,
  maxHp,
  setSanity,
  maxSp,

  playerDeckIds,
  setPlayerDeckIds,
  ownedRelics,
  setOwnedRelics,
  setRelics,
  freeChestsRemaining,
  setFreeChestsRemaining,

  setShrineMaxHpPenalty,
  setShrineMaxSpPenalty,
  nextCombatCost0,
  setNextCombatCost0,
  nextCombatNegateNextDamage,
  setNextCombatNegateNextDamage,
  nextCombatStartArmor,
  setNextCombatStartArmor,

  bypassEnemiesRemaining,
  setBypassEnemiesRemaining,
  levelCombatMods,
  setCombatEnemyTile,
  setIsCombatActive,
  beginCombatTransition,
  setActiveCombatMods,
  preCombatPosRef,

  setCanRest,
  setIsMerchantOpen,
  setActiveMerchantKey,
  setFloorActionCount,
  setTurns,

  isBrazierLit,
  isSpecialCorruptedEnemyAtTile,
  isYahakaAtTile,
  yahakaCombatEnemyId,
  specialBossEnemyId,
  igniteBrazier,
  descendStairs,
  startStoryline,
  onConsultWitch,
  moveEnemiesTowardsPlayer,

  tarotBypassCombats,
  tarotPlayerStartArmor,
  tarotEnemyStartArmor,
  tarotPlayerStartReflectPct,
  tarotEnemyStartReflectPct,
  tarotMagicModifier,
  tarotPerTileHpHeal,
  tarotTrapTimeFactor,
  tarotEnemiesChase,

  trapActivationChanceMultiplier,
  perTileHpRegen,
  TRINKET_LIMIT,

  playDoorBarredSfx,
  playDoorUnlockingSfx,
  playDoorOpenSfx,
  playPlayerStepSfx,
  playTrapCountdownStartSfx,
  playChestUnlockingSfx,
  playChestOpenSfx,
  playLoreInspectSfx,
  playBrazierLitSfx,
}) {
  // ---------------------------------
  // interaction state
  // ---------------------------------
  const [shrinesByKey, setShrinesByKey] = useState({});
  const [isShrineOpen, setIsShrineOpen] = useState(false);
  const [activeShrinePos, setActiveShrinePos] = useState(null);
  const [shrineError, setShrineError] = useState(null);
  const [shrineResult, setShrineResult] = useState(null);

  const [isWellOpen, setIsWellOpen] = useState(false);
  const [activeWellPos, setActiveWellPos] = useState(null);
  const [wellError, setWellError] = useState(null);
  const [wellResult, setWellResult] = useState(null);

  const [trapQTE, setTrapQTE] = useState(null);

  const [activeLore, setActiveLore] = useState(null);
  const [activeLoreBinding, setActiveLoreBinding] = useState(null);
  const [barredDoorLore, setBarredDoorLore] = useState(null);
  const [pendingBlackhandContractAmbush, setPendingBlackhandContractAmbush] = useState(false);

  const [isRansackedChestOpen, setIsRansackedChestOpen] = useState(false);
  const [pendingRansackedChestPos, setPendingRansackedChestPos] = useState(
  initialPendingRansackedChestPos
);
  const [simpleChestRelicPickerMode, setSimpleChestRelicPickerMode] = useState(null);
  const [simpleChestResourceReward, setSimpleChestResourceReward] = useState(null);

const [isWellEchoOpen, setIsWellEchoOpen] = useState(
  initialIsWellEchoOpen
);

const [pendingWellMythicPos, setPendingWellMythicPos] = useState(
  initialPendingWellMythicPos
);

const previousFloorLevelRef = useRef(floorLevel);

  useEffect(() => {
    setShrinesByKey(initShrinesForMap(map, runSeedRef.current, floorLevel));
  }, [map, floorLevel, runSeedRef]);

useEffect(() => {
  // Do not wipe hydrated floor-local state on first mount.
  if (previousFloorLevelRef.current === floorLevel) return;

  previousFloorLevelRef.current = floorLevel;

  // True floor-change reset only.
  setIsShrineOpen(false);
  setActiveShrinePos(null);
  setShrineError(null);
  setShrineResult(null);

  setIsWellOpen(false);
  setActiveWellPos(null);
  setWellError(null);
  setWellResult(null);

  setTrapQTE(null);

  setActiveLore(null);
  setActiveLoreBinding(null);
  setBarredDoorLore(null);
  setPendingBlackhandContractAmbush(false);

  setIsRansackedChestOpen(false);
  setPendingRansackedChestPos(null);
  setSimpleChestRelicPickerMode(null);
  setSimpleChestResourceReward(null);

  setIsWellEchoOpen(false);
  setPendingWellMythicPos(null);
}, [floorLevel]);

  // ---------------------------------
  // derived shrine values
  // ---------------------------------
  const activeShrine = useMemo(() => {
    if (!activeShrinePos) return null;
    return shrinesByKey[`${activeShrinePos.x},${activeShrinePos.y}`] || null;
  }, [activeShrinePos, shrinesByKey]);

  const activeShrineTier = activeShrine?.tier ?? null;

  const activeShrineCostText = activeShrine
    ? formatShrineCost(activeShrine.cost)
    : '';

  const activeShrineRewardText = activeShrineTier
    ? getPlaceholderShrineReward(activeShrineTier)?.label || ''
    : '';

  const activeShrineCanAfford =
    !!activeShrine?.cost &&
    !!activeShrine?.tier &&
    canAffordShrineCost({
      cost: activeShrine.cost,
      resources: { teeth, keys, potions, torches, maxHp, maxSp },
      deckIds: playerDeckIds,
    });

  const chestOnTile = useMemo(() => {
    const tileHere = map?.[playerPos.y]?.[playerPos.x];
    return (
      tileHere === 'chest-locked' ||
      tileHere === 'chest-closed' ||
      tileHere === 'chest-simple-locked' ||
      tileHere === 'chest-simple-closed' ||
      tileHere === 'chest-rare-locked' ||
      tileHere === 'chest-rare-closed'
    );
  }, [map, playerPos.x, playerPos.y]);

  const activeSanctifiedStatueSecret = useMemo(
    () => getActiveSanctifiedStatueSecret(map),
    [map]
  );

  const canInspectSanctifiedSecretStatue = useMemo(() => {
    return isCurrentTileSanctifiedSecretTargetStatue(map, playerPos);
  }, [map, playerPos]);

  // ---------------------------------
  // tiny local helpers
  // ---------------------------------
  const removeFirstOccurrence = (arr, value) => {
    const out = Array.isArray(arr) ? [...arr] : [];
    const idx = out.indexOf(value);
    if (idx >= 0) out.splice(idx, 1);
    return out;
  };

  const removeFirstRelicById = (arr, relicId) => {
  const out = Array.isArray(arr) ? [...arr] : [];
    const idx = out.findIndex((r) => r?.id === relicId);
    if (idx >= 0) out.splice(idx, 1);
    return out;
  };

  const closeRansackedChest = () => {
    setIsRansackedChestOpen(false);
  };

  const rollSimpleChestResourceReward = (rewardRng) => {
    const remainingKinds = [...SIMPLE_CHEST_RESOURCE_KINDS];
    const rewards = [];

    const takeKind = () => {
      if (!remainingKinds.length) return null;
      const idx = Math.floor(rewardRng() * remainingKinds.length);
      return remainingKinds.splice(idx, 1)[0] || null;
    };

    const firstKind = takeKind();
    if (firstKind) {
      rewards.push({ kind: firstKind, amount: 2 + Math.floor(rewardRng() * 4) });
    }

    if (remainingKinds.length && rewardRng() < 0.5) {
      const secondKind = takeKind();
      if (secondKind) {
        rewards.push({ kind: secondKind, amount: 1 + Math.floor(rewardRng() * 3) });
      }

      if (remainingKinds.length && rewardRng() < 0.25) {
        const thirdKind = takeKind();
        if (thirdKind) {
          rewards.push({ kind: thirdKind, amount: 1 + Math.floor(rewardRng() * 2) });
        }

        if (remainingKinds.length && rewardRng() < 0.10) {
          const fourthKind = takeKind();
          if (fourthKind) {
            rewards.push({ kind: fourthKind, amount: 1 });
          }
        }
      }
    }

    return rewards;
  };

  const lootSimpleChestResources = () => {
    const rewards = Array.isArray(simpleChestResourceReward?.rewards)
      ? simpleChestResourceReward.rewards
      : [];

    if (!rewards.length) {
      setSimpleChestResourceReward(null);
      return;
    }

    for (const reward of rewards) {
      const amount = Math.max(0, Math.floor(Number(reward?.amount) || 0));
      if (amount <= 0) continue;

      if (reward.kind === 'keys') {
        setKeys((k) => Math.min(maxKeys, k + amount));
      } else if (reward.kind === 'potions') {
        setPotions((p) => Math.min(maxPotions, p + amount));
      } else if (reward.kind === 'teeth') {
        setTeeth((t) => Math.min(maxTeeth, t + amount));
      } else if (reward.kind === 'torches') {
        setTorches((t) => Math.min(maxTorches, t + amount));
      }
    }

    setSimpleChestResourceReward(null);
  };

  const closeLore = () => {
    setActiveLore(null);
    setActiveLoreBinding(null);

    if (!pendingBlackhandContractAmbush) return;

    setPendingBlackhandContractAmbush(false);

    if (!isBlackhandBossEncounterSelection(specialBossEnemyId)) return;

    setMap((prev) => {
      if (!Array.isArray(prev) || !prev.length) return prev;

      const next = prev.map((row) => row.slice());
      const spawnTile = next?.[BLACKHAND_BOSS_AMBUSH_POS.y]?.[BLACKHAND_BOSS_AMBUSH_POS.x];

      if (spawnTile == null || spawnTile === 'enemy-boss' || spawnTile === 'steps-down') {
        return prev;
      }

      next[BLACKHAND_BOSS_AMBUSH_POS.y][BLACKHAND_BOSS_AMBUSH_POS.x] = 'enemy-boss';
      return next;
    });
  };

  const closeBarredDoorLore = () => {
    setBarredDoorLore(null);
  };

    useEffect(() => {
    const currentTile = map?.[playerPos.y]?.[playerPos.x];

    const canKeepLoreOpenOnCurrentTile =
    (typeof currentTile === 'string' && currentTile.startsWith('bookshelf-')) ||
    (pendingBlackhandContractAmbush &&
      isBlackhandBossEncounterSelection(specialBossEnemyId) &&
      playerPos.x === BLACKHAND_CHEST_POS.x &&
      playerPos.y === BLACKHAND_CHEST_POS.y &&
      currentTile === 'chest-open');

  if (activeLore && activeLoreBinding === 'tile' && !canKeepLoreOpenOnCurrentTile) {
      setActiveLore(null);
      setActiveLoreBinding(null);
    }
  }, [
    map,
    playerPos.x,
    playerPos.y,
    activeLore,
    activeLoreBinding,
    pendingBlackhandContractAmbush,
    specialBossEnemyId,
  ]);
 
  const openShrineAt = (x, y) => {
    setShrineError(null);
    setShrineResult(null);
    setActiveShrinePos({ x, y });
    setIsShrineOpen(true);
  };
  
  const closeShrine = () => {
    setIsShrineOpen(false);
    setActiveShrinePos(null);
    setShrineError(null);
    setShrineResult(null);
  };
  
  const openWellAt = (x, y) => {
    setWellError(null);
    setWellResult(null);
    setActiveWellPos({ x, y });
    setIsWellOpen(true);
  };
  
  const closeWell = () => {
    setIsWellOpen(false);
    setActiveWellPos(null);
    setWellError(null);
    setWellResult(null);
  };
  
  const openRansackedChest = () => {
    setIsRansackedChestOpen(true);
  };
  
  const inspectRansackedChestReward = () => {
    const relic = relicsPool.find((r) => r.id === RANSACKED_CHEST_RELIC_ID);
    if (!relic) return;
  
    setIsRansackedChestOpen(false);
    setPendingRansackedChestPos({ x: playerPos.x, y: playerPos.y });
    setRelics([relic]);
  };
  
  const openWellMythicReward = (wellPos) => {
    if (!wellPos) return;
  
    const eligible = relicsPool.filter(
      (r) =>
        r &&
        r.id &&
        r.rarity === 'mythic' &&
        !ownedRelics.some((o) => o.id === r.id)
    );
  
    if (!eligible.length) {
      setPendingWellMythicPos(null);
      return;
    }
  
    const rng = makeRng(
      (
        (runSeedRef.current ?? 0) ^
        (floorLevel * 0x7f4a7c15) ^
        (wellPos.x * 0x9e37) ^
        (wellPos.y * 0x85eb)
      ) >>> 0
    );
  
    const picks = pickRelicsByRarity(
      eligible,
      Math.min(3, eligible.length),
      rng,
      { common: 0, uncommon: 0, rare: 0, mythic: 1 }
    );
  
    setRelics(picks);
  };

  const confirmWellDiscard = ({ selectedRelicId } = {}) => {
  if (!activeWellPos) return;

  if (!selectedRelicId) {
    setWellError('Choose a trinket to discard.');
    return;
  }

  const chosen = (Array.isArray(ownedRelics) ? ownedRelics : []).find(
    (r) => r?.id === selectedRelicId
  );

  if (!chosen) {
    setWellError('That trinket is no longer in your satchel.');
    return;
  }

  setWellError(null);

  // SPECIAL CASE: the old coin triggers the mythic well event
  if (selectedRelicId === RANSACKED_CHEST_RELIC_ID) {
    const wellPos = activeWellPos;

    setOwnedRelics((prev) => removeFirstRelicById(prev, selectedRelicId));
    setPendingWellMythicPos(wellPos);

    closeWell();
    setIsWellEchoOpen(true);
    return;
  }

  // NORMAL WELL BEHAVIOUR
  setOwnedRelics((prev) => removeFirstRelicById(prev, selectedRelicId));

  setMap((prev) => {
    const next = prev.map((row) => row.slice());
    if (next?.[activeWellPos.y]?.[activeWellPos.x] === 'well') {
      next[activeWellPos.y][activeWellPos.x] = 'floor';
    }
    return next;
  });

  setWellResult(
    `You drop ${chosen.name || 'the trinket'} into the well.\n\nIt sinks without a sound.`
  );
};

const confirmShrineOffer = ({ selectedCardId } = {}) => {
  if (!activeShrinePos) return;

  const key = `${activeShrinePos.x},${activeShrinePos.y}`;
  const shrine = shrinesByKey?.[key];
  if (!shrine) return;

  const cost = shrine.cost;
  const tier = shrine.tier;

  const resources = {
    teeth,
    keys,
    potions,
    torches,
    maxHp,
    maxSp,
  };

  if (!canAffordShrineCost({ cost, resources, deckIds: playerDeckIds })) {
    setShrineError("You can't afford this sacrifice.");
    return;
  }

  if (cost?.kind === 'deckCard' && !selectedCardId) {
    setShrineError('Choose a card to sacrifice.');
    return;
  }

  setShrineError(null);

  // -----------------
  // Apply payment
  // -----------------
  const amt = Math.max(0, Math.floor(Number(cost?.amount) || 0));

  switch (cost?.kind) {
    case 'teeth':
      setTeeth((t) => Math.max(0, t - amt));
      break;
    case 'key':
      setKeys((k) => Math.max(0, k - amt));
      break;
    case 'potion':
      setPotions((p) => Math.max(0, p - amt));
      break;
    case 'torch':
      setTorches((t) => Math.max(0, t - amt));
      break;
    case 'maxHp':
      setShrineMaxHpPenalty((n) => Math.max(0, (Number(n) || 0) + amt));
      break;
    case 'maxSp':
      setShrineMaxSpPenalty((n) => Math.max(0, (Number(n) || 0) + amt));
      break;
    case 'deckCard':
      setPlayerDeckIds((ids) => removeFirstOccurrence(ids, selectedCardId));
      break;
    default:
      break;
  }

  // -----------------
  // Apply placeholder reward
  // -----------------
  const reward = getPlaceholderShrineReward(tier);
  let rewardLine = reward?.label || 'The shrine accepts your offering.';

  if (reward?.type === 'combat' && reward?.perk === 'nextCost0') {
    const a = Math.max(1, Math.floor(Number(reward.amount) || 1));
    setNextCombatCost0((v) => Math.max(Number(v) || 0, a));
  } else if (reward?.type === 'combat' && reward?.perk === 'negateNextDamage') {
    const a = Math.max(1, Math.floor(Number(reward.amount) || 1));
    setNextCombatNegateNextDamage((v) => Math.max(Number(v) || 0, a));
  } else if (reward?.type === 'resource') {
    const a = Math.max(1, Math.floor(Number(reward.amount) || 1));
    const kind = reward.kind;
    if (kind === 'teeth') setTeeth((t) => Math.max(0, Math.min(maxTeeth, t + a)));
    if (kind === 'keys') setKeys((k) => Math.max(0, Math.min(maxKeys, k + a)));
    if (kind === 'potions') setPotions((p) => Math.max(0, Math.min(maxPotions, p + a)));
    if (kind === 'torches') setTorches((t) => Math.max(0, Math.min(maxTorches, t + a)));
  } else if (reward?.type === 'relic') {
    // Auto-grant a relic (avoids adding a new picker flow for now).
    if (ownedRelics.length >= TRINKET_LIMIT) {
      rewardLine = 'The shrine tries to grant a trinket… but your satchel is full.';
    } else {
      const pool = relicsPool.filter(
  (r) => !ownedRelics.some((o) => o.id === r.id) && !isRansackedChestExclusiveRelic(r)
);
      const rng = makeRng(
        (runSeedRef.current ^
          (floorLevel * 0x7f4a7c15) ^
          (activeShrinePos.x * 0x9e37) ^
          (activeShrinePos.y * 0x85eb)) >>> 0
      );

      const chosen = pickRelicsByRarity(pool, 1, rng, reward.rarityWeights)?.[0];

      if (chosen) {
        setOwnedRelics((prev) => [...prev, chosen]);
        rewardLine = `${reward?.label || 'Mythic Boon'}\n\nYou received: ${chosen.name}`;
      } else {
        rewardLine = "The shrine's boon fizzles (no eligible trinkets found).";
      }
    }
  }

  // Remove the shrine tile after use.
  setMap((prev) => {
    const next = prev.map((row) => row.slice());
    if (next?.[activeShrinePos.y]?.[activeShrinePos.x]?.startsWith('shrine-')) {
      next[activeShrinePos.y][activeShrinePos.x] = 'floor';
    }
    return next;
  });

  setShrineResult(rewardLine);
};

 const handleLoreClick = () => {
   playLoreInspectSfx();

   const tileHere = map?.[playerPos.y]?.[playerPos.x];

   if (isSanctifiedSecretLoreTile(tileHere)) {
     const secretLore = getSanctifiedSecretLoreText(tileHere);
     setActiveLore(secretLore);
     setActiveLoreBinding('free');
     setMap((prev) => markSanctifiedSecretLoreRead(prev));
     return;
   }

    const loreEntry = getLoreEntryForTile({
      tile: tileHere,
      x: playerPos.x,
      y: playerPos.y,
      floorLevel,
      runSeed: runSeedRef.current,
    });

    setActiveLore(loreEntry?.text || '“The pages have been gnawed blank by time.”');
    setActiveLoreBinding('tile');
   };

  const inspectSanctifiedSecretStatue = () => {
    if (!canInspectSanctifiedSecretStatue || !activeSanctifiedStatueSecret) return;

    const eligible = relicsPool.filter(
      (r) =>
        r &&
        r.id &&
        r.rarity === 'mythic' &&
        !ownedRelics.some((o) => o.id === r.id)
    );

    if (!eligible.length) {
      setActiveLore('A hidden compartment yawns open within the statue, but it has already been emptied.');
      setActiveLoreBinding('free');
      return;
    }

    const rng = makeRng(
      (
        (runSeedRef.current ?? 0) ^
        (floorLevel * 0x6d2b79f5) ^
        ((activeSanctifiedStatueSecret.targetX + 1) * 0x9e37) ^
        ((activeSanctifiedStatueSecret.targetY + 1) * 0x85eb) ^
        ((activeSanctifiedStatueSecret.statueId + 1) * 0xc2b2ae35)
      ) >>> 0
    );

    const picks = pickRelicsByRarity(
      eligible,
      Math.min(3, eligible.length),
      rng,
      { common: 0, uncommon: 0, rare: 0, mythic: 1 }
    );

    if (!picks.length) {
      setActiveLore('The statue conceals a cache, but nothing answers your touch.');
      setActiveLoreBinding('free');
      return;
    }

    setMap((prev) => setSanctifiedSecretOfferPending(prev, true));
    setRelics(picks);
  };

  const resolveTrap = (success) => {
    const pos = trapQTE;
    if (!pos) return;
  
    // Clear the trap so it can't retrigger.
    setMap((prev) => {
      const next = prev.map((row) => row.slice());
      if (next?.[pos.y]?.[pos.x] === 'trap') {
        const isBlackhandFinalCorridorTrap =
          isBlackhandBossEncounterSelection(specialBossEnemyId) &&
          pos.x === 10 &&
          (pos.y === 39 || pos.y === 40);

        next[pos.y][pos.x] = isBlackhandFinalCorridorTrap ? 'corridor' : 'floor';
      }
      return next;
    });
  
    if (success) {
    // Reward: 30% chance to gain 1–2 teeth + a one-shot combat perk.
    if (Math.random() < 0.30) {
      const gain = 1 + Math.floor(Math.random() * 2); // 1 or 2
      setTeeth((t) => Math.min(maxTeeth, t + gain));
    }
    setNextCombatCost0((n) => Math.max(n, 1));
  } else {
  
      // Fail: hurt HP, no consolation prize.
      setHp((h) => Math.max(0, h - 6));
    }
  
    setTrapQTE(null);
  };

  const unlockChest = () => {
    if (!chestOnTile) return;

    const tileHere = map[playerPos.y][playerPos.x];
    const isSimpleChest =
      tileHere === 'chest-simple-locked' || tileHere === 'chest-simple-closed';
    const isRareChest =
      tileHere === 'chest-rare-locked' || tileHere === 'chest-rare-closed';
    const isLockedChest =
      tileHere === 'chest-locked' ||
      tileHere === 'chest-simple-locked' ||
      tileHere === 'chest-rare-locked';
    const isAlreadyUnlockedChest =
      tileHere === 'chest-closed' ||
      tileHere === 'chest-simple-closed' ||
      tileHere === 'chest-rare-closed';

    if (isLockedChest && !(keys > 0 || freeChestsRemaining > 0)) return;

    if (isLockedChest) {
      playChestUnlockingSfx();
    } else if (isAlreadyUnlockedChest) {
      playChestOpenSfx();
    }

    const newMap = map.map((row) => [...row]);
    const openedTile = isRareChest
      ? 'chest-rare-open'
      : isSimpleChest
        ? 'chest-simple-open'
        : 'chest-open';

    newMap[playerPos.y][playerPos.x] = openedTile;
    setMap(newMap);

    if (isLockedChest) {
      if (freeChestsRemaining > 0) {
        setFreeChestsRemaining((n) => Math.max(0, n - 1));
      } else {
        setKeys((k) => Math.max(0, k - 1));
      }
    }

    const isBlackhandContractChest =
      isBlackhandBossEncounterSelection(specialBossEnemyId) &&
      playerPos.x === BLACKHAND_CHEST_POS.x &&
      playerPos.y === BLACKHAND_CHEST_POS.y;

    if (isBlackhandContractChest) {
      setTurns((t) => t + 1);
      setFloorActionCount((n) => n + 1);
      setPendingBlackhandContractAmbush(true);
      setActiveLore(BLACKHAND_CONTRACT_LORE);
      setActiveLoreBinding('free');
      playLoreInspectSfx();
      return;
    }

    setTurns((t) => t + 1);
    setFloorActionCount((n) => n + 1);

    // Relic hooks: opening a chest can grant immediate boons. Use applyChestHooks for consistency.
    applyChestHooks(ownedRelics, {
      addSanity: (n) => setSanity((s) => s + n),
      addHp: (n) => setHp((h) => Math.min(maxHp, h + n)),
      addKeys: (n) => setKeys((k) => Math.min(maxKeys, k + n)),
      addTeeth: (n) => setTeeth((t) => Math.min(maxTeeth, t + n)),
      addPotions: (n) => setPotions((p) => Math.min(maxPotions, p + n)),
      addNextCombatCost0: (n) => setNextCombatCost0((v) => Math.max(v, n)),
    });

    const available = relicsPool.filter(
      (r) => !ownedRelics.some((o) => o.id === r.id) && !isRansackedChestExclusiveRelic(r)
    );

    const rewardRng = makeRng(
      (
        (runSeedRef.current ?? 0) ^
        (floorLevel * 0x9e3779b9) ^
        (playerPos.x * 0x85ebca6b) ^
        (playerPos.y * 0xc2b2ae35) ^
        (isRareChest ? 0x51ed270b : isSimpleChest ? 0x6f4ad92d : 0x27d4eb2d)
      ) >>> 0
    );

    if (isSimpleChest) {
      const commonAvailable = available.filter((r) => getRelicRarity(r) === 'common');
      const shouldOfferRelic = commonAvailable.length > 0 && rewardRng() < 0.5;

      setSimpleChestResourceReward(null);

      if (shouldOfferRelic) {
        const picks = pickRelicsByRarity(
          commonAvailable,
          Math.min(1, commonAvailable.length),
          rewardRng,
          { common: 1, uncommon: 0, rare: 0, mythic: 0 }
        );

        setSimpleChestRelicPickerMode('simple-chest');
        setRelics(picks);
        return;
      }

      setSimpleChestRelicPickerMode(null);
      setRelics([]);
      setSimpleChestResourceReward({
        pos: { x: playerPos.x, y: playerPos.y },
        rewards: rollSimpleChestResourceReward(rewardRng),
      });
      return;
    }

    const eligible = isRareChest
      ? available.filter((r) => getRelicRarity(r) === 'rare')
      : available;

    const picks = pickRelicsByRarity(
      eligible.length ? eligible : available,
      Math.min(3, (eligible.length ? eligible : available).length),
      rewardRng,
      isRareChest
        ? { common: 0, uncommon: 0, rare: 1 }
        : { common: 0.7, uncommon: 0.25, rare: 0.05 }
    );

    setSimpleChestRelicPickerMode(null);
    setSimpleChestResourceReward(null);
    setRelics(picks);
  };

      const hasBlockingOverlay =
    isStatusOpen ||
    isMerchantOpen ||
    isShrineOpen ||
    isWellOpen ||
    isStorylineActive ||
    isCombatActive ||
    isPreCombatTransitionActive ||
    !!trapQTE ||
    !!pendingDeckCullReward ||
    !!activeLore ||
    !!barredDoorLore ||
    isRansackedChestOpen ||
    isWellEchoOpen ||
    !!simpleChestResourceReward;

  const handleTap = () => {
  if (hasBlockingOverlay) return;
  if (isMerchantOpen) return;
  if (pendingDeckCullReward) return;

  const currentTile = map[playerPos.y][playerPos.x];

  if (currentTile === 'rest') {
    setCanRest(true);
  } else if (currentTile === 'brazier') {
  if (!BRAZIER_LIGHTING_ENABLED) return;
  if (isBrazierLit(playerPos.x, playerPos.y)) return;

  igniteBrazier(playerPos.x, playerPos.y);

  // ✅ NEW: reward SP for lighting a brazier (+15)
  setSanity((s) => s + 15);

  playBrazierLitSfx();
  setTurns((t) => t + 1); // spend one turn to ignite
  setFloorActionCount((n) => n + 1);
    } else if (
    currentTile === 'chest-locked' ||
    currentTile === 'chest-closed' ||
    currentTile === 'chest-simple-locked' ||
    currentTile === 'chest-simple-closed' ||
    currentTile === 'chest-rare-locked' ||
    currentTile === 'chest-rare-closed'
  ) {
    unlockChest();
  } else if (typeof currentTile === 'string' && currentTile.startsWith('shrine-')) {
    openShrineAt(playerPos.x, playerPos.y);
  } else if (currentTile === RANSACKED_CHEST_TILE) {
    openRansackedChest();
  } else if (currentTile === 'merchant') {
    setActiveMerchantKey(`${playerPos.x},${playerPos.y}`);
    setIsMerchantOpen(true);
  } else if (currentTile === 'well') {
    openWellAt(playerPos.x, playerPos.y);
  } else if (currentTile === 'witch') {
    onConsultWitch?.();
  } else if (currentTile.startsWith('bookshelf-')) {
    handleLoreClick();
  } else if (currentTile === MONSIGNOR_LIBERATED_GUARD_TILE) {
    playLoreInspectSfx();
    setBarredDoorLore(MONSIGNOR_LIBERATED_PENITENT_LORE);
  } else if (currentTile === 'steps-down') {
    descendStairs();
  } else if (currentTile === 'storyline') {
    startStoryline();
  }
};

  const handleTileClick = (x, y) => {
  if (hasBlockingOverlay) return;
  if (isStatusOpen) return;
  if (isMerchantOpen) return;
  if (isMiniMapOpen) return;
  if (isShrineOpen) return;
  if (isWellOpen) return;
  if (isStorylineActive || isCombatActive || isPreCombatTransitionActive || trapQTE || pendingDeckCullReward) return;

  const dx = x - playerPos.x;
  const dy = y - playerPos.y;

  const manhattan = Math.abs(dx) + Math.abs(dy);

  // Tap current tile = do the normal interact logic you already have
  if (manhattan === 0) {
    handleTap();
    return;
  }

  // Only adjacent (up/down/left/right)
  if (manhattan !== 1) return;

  // Reuse existing movement logic (walkable checks, combat triggers, etc.)
  handleMove(dx, dy);
};

  const handleMove = (dx, dy) => {
    if (hasBlockingOverlay) return;
      if (isStatusOpen) return;
  
      if (isStorylineActive || isCombatActive || isPreCombatTransitionActive || trapQTE || pendingDeckCullReward) return;
    
      const targetX = playerPos.x + dx;
      const targetY = playerPos.y + dy;
      
      // Check bounds first
      if (targetX < 0 || targetX >= mapWidth || targetY < 0 || targetY >= mapHeight) return;
      
      const currentTile = map[playerPos.y][playerPos.x];
      const targetTile = map[targetY][targetX];
    
      // Handle merchant closing when moving away
if (currentTile === 'merchant' && targetTile !== 'merchant') {
  setIsMerchantOpen(false);
  setActiveMerchantKey(null);
}
    
      // Handle different tile types
      const targetKey = `${targetX},${targetY}`;
  
      if ((targetTile === 'door-locked' || targetTile === 'door-closed') && barredDoorKeys.has(targetKey)) {
        playDoorBarredSfx();
        setBarredDoorLore(pickBarredDoorPopupText());
        return;
      }
  
          if (targetTile === 'door-locked' && keys > 0) {
        playDoorUnlockingSfx();
  
        const newMap = map.map(row => row.slice());
        newMap[targetY][targetX] = 'door-open';
        setMap(newMap);
        setKeys(k => k - 1);
      }
      else if (targetTile === 'door-closed') {
  playDoorOpenSfx();

  const newMap = map.map((row) => row.slice());
  newMap[targetY][targetX] = 'door-open';
  setMap(newMap);

  setPlayerPos({ x: targetX, y: targetY });
  playPlayerStepSfx();
  setFloorActionCount((n) => n + 1);

  let isInBrazierLight = false;
  for (let y = 0; y < mapHeight; y++) {
    for (let x = 0; x < mapWidth; x++) {
      if (BRAZIER_LIGHTING_ENABLED && map[y][x] === 'brazier' && isBrazierLit(x, y)) {
        const dx = Math.abs(x - targetX);
        const dy = Math.abs(y - targetY);
        if (dx + dy <= BRAZIER_LIGHT_RADIUS) {
          isInBrazierLight = true;
          break;
        }
      }
    }
    if (isInBrazierLight) break;
  }

  const steppedOnCorruption = isTileCorrupted(floorCorruption, targetX, targetY);

  if (steppedOnCorruption) {
    setTurns((t) => t + 1);
    setSanity((s) => Math.max(0, s - 1));
  } else if (!isInBrazierLight) {
    setTurns((t) => t + 1);
  }

  setCanRest(false);

  if (tarotEnemiesChase) {
    setMap((prev) => moveEnemiesTowardsPlayer(prev, { x: targetX, y: targetY }));
  }

  return;
}
      else if (targetTile === 'enemy' || targetTile === 'enemy-boss') {
        const isYahakaEncounter = !!isYahakaAtTile?.(targetX, targetY);
        // If the player has a bypass charge (e.g. Stealth Ring) or the current
        // tarot allows unlimited skips, slip past the enemy without combat.
          if (!isYahakaEncounter && targetTile === 'enemy' && (bypassEnemiesRemaining > 0 || tarotBypassCombats)) {
    const newMap = map.map((row) => row.slice());
    newMap[targetY][targetX] = 'floor';
    setMap(newMap);

    setPlayerPos({ x: targetX, y: targetY });
    playPlayerStepSfx();
    setFloorActionCount((n) => n + 1);

    // Per-tile healing should still apply because this is still movement.
    const healAmt = (perTileHpRegen || 0) + (tarotPerTileHpHeal || 0);
    if (healAmt > 0) {
      setHp((h) => h + healAmt);
    }

    // Consume a bypass charge only for the normal per-floor charge.
    if (!tarotBypassCombats) {
      setBypassEnemiesRemaining((n) => Math.max(0, n - 1));
    }

    let isInBrazierLight = false;
    for (let y = 0; y < mapHeight; y++) {
      for (let x = 0; x < mapWidth; x++) {
        if (BRAZIER_LIGHTING_ENABLED && map[y][x] === 'brazier' && isBrazierLit(x, y)) {
          const dxm = Math.abs(x - targetX);
          const dym = Math.abs(y - targetY);
          if (dxm + dym <= BRAZIER_LIGHT_RADIUS) {
            isInBrazierLight = true;
            break;
          }
        }
      }
      if (isInBrazierLight) break;
    }

    const steppedOnCorruption = isTileCorrupted(floorCorruption, targetX, targetY);

    if (steppedOnCorruption) {
      setTurns((t) => t + 1);
      setSanity((s) => Math.max(0, s - 1));
    } else if (!isInBrazierLight) {
      setTurns((t) => t + 1);
    }

    setCanRest(false);

    if (tarotEnemiesChase) {
      setMap((prev) => moveEnemiesTowardsPlayer(prev, { x: targetX, y: targetY }));
    }

    return;
  }
  
        // Otherwise, trigger combat FROM the current tile (do not move onto the enemy).
        // This avoids getting stuck when returning to the dungeon while the enemy still exists.
        preCombatPosRef.current = { ...playerPos };

const isSpecialCorruptedEnemy =
  targetTile === 'enemy' &&
  !!isSpecialCorruptedEnemyAtTile?.(targetX, targetY);

const specialBossTileEnemyId = getSpecialBossFloorEnemyIdAtTile(
  specialBossEnemyId,
  {
    x: targetX,
    y: targetY,
    tile: targetTile,
  }
);

const enemyId =
  targetTile === 'enemy-boss'
    ? (typeof specialBossEnemyId === 'string' && specialBossEnemyId.trim()
        ? specialBossEnemyId.trim()
        : 'nested_horror')
    : specialBossTileEnemyId
      ? specialBossTileEnemyId
      : isYahakaEncounter
        ? (typeof yahakaCombatEnemyId === 'string' && yahakaCombatEnemyId.trim()
            ? yahakaCombatEnemyId.trim()
            : 'yahaka_voidfeeder_1')
        : isSpecialCorruptedEnemy
          ? CORRUPTED_SPECIAL_ENEMY_ID
          : getEnemyIdForTile({
              x: targetX,
              y: targetY,
              floor: floorLevel,
              runSeed: runSeedRef.current,
              map,
            });

        // Snapshot combat mods at the moment combat starts (consumes one-shot flags).
      const passiveMods = computeCombatMods(ownedRelics);
  
      const oneShotCost0 = Math.max(0, Number(nextCombatCost0) || 0);
      const oneShotNegate = Math.max(0, Number(nextCombatNegateNextDamage) || 0);
      const oneShotStartArmor = Math.max(0, Number(nextCombatStartArmor) || 0);
  
          {
            // Merge combat modifiers from relics and level scaling. Numeric values should
            // generally stack additively. However, object spread would override
            // properties (e.g. firstTurnExtraDraw) from relics with level-based values.
            // To ensure relic bonuses are not lost (e.g. Preserved Skull’s extra draw),
            // explicitly sum the firstTurnExtraDraw values.
            const combinedMods = { ...passiveMods, ...levelCombatMods };
            // Combine the first-turn extra draw values from both sources rather than
            // allowing the later spread to override the former.
            combinedMods.firstTurnExtraDraw =
              (Number(passiveMods.firstTurnExtraDraw || 0) || 0) +
              (Number(levelCombatMods.firstTurnExtraDraw || 0) || 0);
            // Apply tarot start armor modifiers for player and enemy.
            if (tarotPlayerStartArmor) {
              combinedMods.startArmor = (Number(combinedMods.startArmor || 0) || 0) + tarotPlayerStartArmor;
            }
            if (tarotEnemyStartArmor) {
              combinedMods.enemyStartArmor = (Number(combinedMods.enemyStartArmor || 0) || 0) + tarotEnemyStartArmor;
            }

            if (tarotPlayerStartReflectPct) {
              combinedMods.startReflectPct =
                (Number(combinedMods.startReflectPct || 0) || 0) + tarotPlayerStartReflectPct;
            }

            if (tarotEnemyStartReflectPct) {
              combinedMods.enemyStartReflectPct =
                (Number(combinedMods.enemyStartReflectPct || 0) || 0) + tarotEnemyStartReflectPct;
            }
            
            if (tarotMagicModifier === 'boost') {
              combinedMods.magicDamageMultiplier =
                Number(combinedMods.magicDamageMultiplier || 1) * 1.25;
            } else if (tarotMagicModifier === 'nerf') {
              combinedMods.magicDamageMultiplier =
                Number(combinedMods.magicDamageMultiplier || 1) * 0.75;
            }

            if (enemyId === MONSIGNOR_BOSS_ENEMY_ID) {
              combinedMods.enemyStartHp =
                (Number(combinedMods.enemyStartHp || 0) || 0) -
                getMonsignorBossHpReduction(map);
            }

            if (enemyId === PRIME_VESSEL_BOSS_ENEMY_ID) {
              combinedMods.enemyStartHp =
                (Number(combinedMods.enemyStartHp || 0) || 0) -
                getPrimeVesselBossHpReduction(map);

              Object.assign(combinedMods, getPrimeVesselCombatStatusMods(map));
            }

            setActiveCombatMods({
              ...combinedMods,
              startArmor: (Number(combinedMods.startArmor || 0) || 0) + oneShotStartArmor,
  
              // existing one-shot
              startNextCardCost0: Math.max(passiveMods.startNextCardCost0 || 0, oneShotCost0),
  
              // ✅ NEW one-shot: negate next incoming damage instance
              startNegateNextDamage:
                (Number(passiveMods.startNegateNextDamage || 0) || 0) + oneShotNegate,
            });
          }
  
      if (oneShotCost0 > 0) setNextCombatCost0(0);
      if (oneShotNegate > 0) setNextCombatNegateNextDamage(0);
      if (oneShotStartArmor > 0) setNextCombatStartArmor(0);

const nextCombatEnemyTile = { x: targetX, y: targetY, floor: floorLevel, enemyId };

if (typeof beginCombatTransition === 'function') {
  beginCombatTransition(nextCombatEnemyTile);
} else {
  setCombatEnemyTile(nextCombatEnemyTile);
  setIsCombatActive(true);
}
  
      }
      
      else if (isWalkable(targetTile)) {
      setPlayerPos({ x: targetX, y: targetY });
      playPlayerStepSfx();
      setFloorActionCount((n) => n + 1);
        // Per‑tile healing from relics and tarot. Both sources stack additively.
        const healAmt = (perTileHpRegen || 0) + (tarotPerTileHpHeal || 0);
        if (healAmt > 0) {
          setHp((h) => h + healAmt);
        }
  
             // Trap tile: chance to trigger a quick-time disarm overlay when stepped on.
      if (targetTile === 'trap') {
      // Base chance is 100% unless modified by relics
      const chanceToTrigger = Math.max(0, Math.min(1, 1 * trapActivationChanceMultiplier));
  
      if (Math.random() < chanceToTrigger) {
      // Duration scales slightly with floor (gets a bit tighter later).
      const baseMs = 1250;
      const minMs = 850;
      // Tarot can shorten the duration multiplicatively.
      const durationMs = Math.max(
        minMs,
        (baseMs - (floorLevel - 1) * 40) * (tarotTrapTimeFactor || 1)
      );
          playTrapCountdownStartSfx();
      setTrapQTE({ x: targetX, y: targetY, durationMs });
      } else {
      // Boots prevented the trigger — do nothing (you still moved onto the tile).
      // Optional: add a small feedback popup if you have one.
      }
      }
  
        
        let isInBrazierLight = false;
        for (let y = 0; y < mapHeight; y++) {
          for (let x = 0; x < mapWidth; x++) {
            if (BRAZIER_LIGHTING_ENABLED && map[y][x] === 'brazier' && isBrazierLit(x, y)) {
              const dx = Math.abs(x - targetX);
              const dy = Math.abs(y - targetY);
              if (dx + dy <= BRAZIER_LIGHT_RADIUS) {
                isInBrazierLight = true;
                break;
              }
            }
          }
          if (isInBrazierLight) break;
        }
        
        const steppedOnCorruption = isTileCorrupted(floorCorruption, targetX, targetY);
  
      if (steppedOnCorruption) {
      // Corrupted ground counts as "dark" even if light reaches it.
      setTurns((t) => t + 1);
     setSanity((s) => Math.max(0, s - 1));
      } else if (!isInBrazierLight) {
      setTurns((t) => t + 1);
      }
        
        const steppedOnRest = targetTile === 'rest';
      setCanRest(steppedOnRest);
  
        // When the tarot Veil card is reversed, enemies pursue the player.
        if (tarotEnemiesChase) {
          setMap((prev) => moveEnemiesTowardsPlayer(prev, { x: targetX, y: targetY }));
        }
  
      } 
        
      };

  // 10) add relic-picker bridge handlers here:
  const confirmWellEchoListen = () => {
    setIsWellEchoOpen(false);
    openWellMythicReward(pendingWellMythicPos);
  };

  const handleDungeonRelicPickerClose = () => {
    if (pendingWellMythicPos) return;

    if (activeSanctifiedStatueSecret?.offered) {
      setMap((prev) => setSanctifiedSecretOfferPending(prev, false));
    }

    setSimpleChestRelicPickerMode(null);
    setRelics([]);
    setPendingRansackedChestPos(null);
  };

  const handleDungeonRelicPick = (relic) => {
    if (ownedRelics.length >= TRINKET_LIMIT) {
      if (activeSanctifiedStatueSecret?.offered) {
        setMap((prev) => setSanctifiedSecretOfferPending(prev, false));
      }
      setRelics([]);
      return;
    }

    if (ownedRelics.some((r) => r.id === relic.id)) {
      if (activeSanctifiedStatueSecret?.offered) {
        setMap((prev) => setSanctifiedSecretOfferPending(prev, false));
      }
      setRelics([]);
      return;
    }

        const ctx = {
      addPotions: (n) =>
        setPotions((p) => Math.min(maxPotions, p + (Number(n) || 0))),
      addSanity: (n) =>
        setSanity((s) => s + (Number(n) || 0)),
      addKeys: (n) =>
        setKeys((k) => Math.min(maxKeys, k + (Number(n) || 0))),
      addTorches: (n) =>
        setTorches((t) => Math.min(maxTorches, t + (Number(n) || 0))),
      addTeeth: (n) =>
        setTeeth((t) => Math.min(maxTeeth, t + (Number(n) || 0))),
      setNextCombatCost0: (n) =>
        setNextCombatCost0((v) => Math.max(v, Number(n) || 0)),
    };

    relic.effect?.(ctx);
    setOwnedRelics((prev) => [...prev, relic]);
    setSimpleChestRelicPickerMode(null);

    if (pendingRansackedChestPos) {
      setMap((prev) => {
        const next = prev.map((row) => [...row]);
        if (
          next[pendingRansackedChestPos.y]?.[pendingRansackedChestPos.x] ===
          RANSACKED_CHEST_TILE
        ) {
          next[pendingRansackedChestPos.y][pendingRansackedChestPos.x] = 'chest-open';
        }
        return next;
      });
      setPendingRansackedChestPos(null);
    }

    if (pendingWellMythicPos) {
      setMap((prev) => {
        const next = prev.map((row) => [...row]);
        if (next[pendingWellMythicPos.y]?.[pendingWellMythicPos.x] === 'well') {
          next[pendingWellMythicPos.y][pendingWellMythicPos.x] = 'floor';
        }
        return next;
      });
      setPendingWellMythicPos(null);
    }

    if (activeSanctifiedStatueSecret?.offered) {
      setMap((prev) => markSanctifiedSecretClaimed(prev));
    }

    setRelics([]);
  };

  return {
    shrinesByKey,
    isShrineOpen,
    activeShrinePos,
    shrineError,
    shrineResult,

    isWellOpen,
    activeWellPos,
    wellError,
    wellResult,

    trapQTE,
    activeLore,
    barredDoorLore,
    isRansackedChestOpen,
    isWellEchoOpen,
    pendingRansackedChestPos,
    pendingWellMythicPos,
    simpleChestRelicPickerMode,
    simpleChestResourceReward,
    
    activeShrineTier,
    activeShrineCostText,
    activeShrineRewardText,
    activeShrineCanAfford,

    openShrineAt,
    closeShrine,
    confirmShrineOffer,

    openWellAt,
    closeWell,
    confirmWellDiscard,

    openRansackedChest,
    closeRansackedChest,
    inspectRansackedChestReward,
    lootSimpleChestResources,

    confirmWellEchoListen,

    canInspectSanctifiedSecretStatue,
    inspectSanctifiedSecretStatue,

    handleLoreClick,
    closeLore,
    closeBarredDoorLore,

    handleTap,
    handleTileClick,
    handleMove,
    resolveTrap,
    unlockChest,

    handleDungeonRelicPick,
    handleDungeonRelicPickerClose,
  };
}